import { useEffect, useState } from "react";
import {
  AdminButton,
  AdminSection,
  CheckboxRow,
  Notice,
  TextInput,
} from "./AdminPrimitives";
import { useTestNotification, useUpdateNotifications } from "../../hooks/useAdmin";
import type { NotificationsDto } from "../../types/admin";

/**
 * Teams 알림 설정.
 *
 * 웹훅 URL 은 서명을 포함하므로 서버가 앞부분만 마스킹해 내려준다. 따라서 입력란은
 * 항상 비어 있고, <strong>값을 입력했을 때만</strong> 덮어쓴다 — 빈 칸으로 저장하면
 * 환경변수(TEAMS_WEBHOOK_URL) 값으로 되돌아간다.
 */
export default function NotificationPanel({
  token,
  notifications,
}: {
  token: string | null;
  notifications: NotificationsDto;
}) {
  const [enabled, setEnabled] = useState(notifications.enabled);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [dashboardUrl, setDashboardUrl] = useState(notifications.dashboardUrl);
  const [saved, setSaved] = useState(false);

  const update = useUpdateNotifications(token);
  const test = useTestNotification(token);

  // 서버 값이 갱신되면 폼을 다시 맞춘다. URL 입력란은 비운 채로 둔다(마스킹 값이라 되돌려 넣을 수 없음).
  useEffect(() => {
    setEnabled(notifications.enabled);
    setDashboardUrl(notifications.dashboardUrl);
    setWebhookUrl("");
  }, [notifications]);

  const dirty =
    enabled !== notifications.enabled ||
    webhookUrl.trim() !== "" ||
    dashboardUrl !== notifications.dashboardUrl;

  return (
    <AdminSection
      title="Teams 알림"
      description="정상(UP) → 데이터 정체(DOWN) 로 바뀐 순간에만 카드를 보냅니다. 복구·미운영 전환은 노이즈라 보내지 않습니다."
    >
      <div className="space-y-4">
        <CheckboxRow
          checked={enabled}
          onChange={setEnabled}
          label="알림 발송 사용"
          hint={
            notifications.webhookConfigured
              ? `현재 웹훅: ${notifications.webhookUrlMasked}${
                  notifications.webhookOverridden ? " (화면에서 설정한 값)" : " (환경변수 값)"
                }`
              : "웹훅 URL 이 설정되지 않아 켜도 발송되지 않습니다."
          }
        />

        <TextInput
          label="웹훅 URL"
          value={webhookUrl}
          onChange={setWebhookUrl}
          placeholder={
            notifications.webhookConfigured
              ? "변경할 때만 입력 (비워 두면 현재 값 유지)"
              : "https://... Power Automate 트리거 URL"
          }
          hint={
            notifications.webhookOverridden
              ? "비운 채로 저장하면 환경변수 TEAMS_WEBHOOK_URL 값으로 되돌아갑니다."
              : "지금은 환경변수 값을 쓰는 중입니다. 값을 입력해 저장하면 그 값이 우선합니다."
          }
        />

        <TextInput
          label="대시보드 링크"
          value={dashboardUrl}
          onChange={setDashboardUrl}
          placeholder="https://status.bridge.watchmile.net"
          hint="알림 카드 하단 '대시보드에서 확인' 버튼이 여는 주소. 비우면 버튼이 생략됩니다."
        />

        {update.error && <Notice tone="error">{update.error.message}</Notice>}
        {saved && !dirty && !update.error && (
          <Notice tone="success">저장했습니다.</Notice>
        )}
        {test.data && (
          <Notice tone={test.data.delivered ? "success" : "error"}>
            {test.data.message}
          </Notice>
        )}
        {test.error && <Notice tone="error">{test.error.message}</Notice>}

        <div className="flex flex-wrap items-center gap-2">
          <AdminButton
            tone="primary"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setSaved(false);
              test.reset();
              update.mutate(
                {
                  enabled,
                  // 빈 문자열 = "덮어쓰지 않음" → 서버가 환경변수 값으로 되돌린다.
                  webhookUrl: webhookUrl.trim(),
                  dashboardUrl: dashboardUrl.trim(),
                },
                { onSuccess: () => setSaved(true) },
              );
            }}
          >
            {update.isPending ? "저장 중…" : "저장"}
          </AdminButton>
          <AdminButton
            disabled={test.isPending || update.isPending}
            onClick={() => {
              setSaved(false);
              test.mutate();
            }}
          >
            {test.isPending ? "발송 중…" : "테스트 발송"}
          </AdminButton>
          {dirty && (
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              테스트는 저장된 설정으로 보냅니다 — 먼저 저장하세요.
            </span>
          )}
        </div>
      </div>
    </AdminSection>
  );
}
